import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSolucaoBySlug, solucoes } from "@/data/solucoes";
import SolucaoMockScreen from "@/components/solucoes/SolucaoMockScreen";
import { cn } from "@/lib/utils";

interface HowItWorksVariant {
  label: string;
  steps: string[];
}

const HowItWorksVariants = ({ variants }: { variants: HowItWorksVariant[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = variants[activeIndex];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {variants.map((variant, index) => (
          <button
            key={variant.label}
            type="button"
            onClick={() => setActiveIndex(index)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition border",
              index === activeIndex
                ? "bg-lime text-lime-foreground border-lime"
                : "bg-background text-foreground border-border hover:border-lime/50"
            )}
          >
            {variant.label}
          </button>
        ))}
      </div>

      <ol className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300" key={active.label}>
        {active.steps.map((step, i) => (
          <li key={i} className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-lime text-lime-foreground flex items-center justify-center flex-shrink-0 font-semibold text-sm">
              {i + 1}
            </div>
            <p className="text-base text-foreground pt-1 leading-relaxed">
              {step}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
};

const SolucaoPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const solucao = getSolucaoBySlug(slug);
  

  if (!solucao) return <Navigate to="/" replace />;

  const Icon = solucao.icon;
  const outras = solucoes.filter((s) => s.slug !== solucao.slug).slice(0, 4);


  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={solucao.seoTitle}
        description={solucao.seoDescription}
        canonical={`https://ameup.com.br/solucoes/${solucao.slug}`}
        ogTitle={solucao.seoTitle}
        ogDescription={solucao.tagline}
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-border/40 bg-white">
          <div className="container mx-auto max-w-6xl px-4 py-16 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="flex flex-col items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                  <Icon className="w-7 h-7 text-lime" />
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-foreground leading-tight">
                  {solucao.title}
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
                  {solucao.tagline}
                </p>
                <p className="text-base text-muted-foreground/90 max-w-xl leading-relaxed">
                  {solucao.description}
                </p>

                {solucao.slug === "programa-de-indicacao" && (
                  <div className="w-full max-w-xl rounded-2xl border border-lime/40 bg-lime/10 p-5 flex items-center gap-4">
                    <div className="text-4xl md:text-5xl font-bold text-lime leading-none">
                      3x
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-foreground">
                        Cliente indicado converte 3x mais
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Indicações convertem em média 3 a 5 vezes mais que canais tradicionais de marketing.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-2">
                  <Button
                    asChild
                    size="lg"
                    className="bg-lime text-lime-foreground hover:bg-lime/90"
                  >
                    <Link to="/cadastro-fornecedor">
                      Agendar uma demonstração
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/materiais-gratuitos">Materiais gratuitos</Link>
                  </Button>
                </div>
              </div>
              <div className="w-full">
                <SolucaoMockScreen slug={solucao.slug} />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10">
              O que você ganha
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {solucao.benefits.map((b) => (
                <Card key={b.title} className={`border-border/60 ${b.wide ? "md:col-span-2" : ""}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-lime" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {b.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {b.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-20 bg-muted/30 border-y border-border/40">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Como funciona
            </h2>

            {solucao.howItWorksVariants ? (
              <HowItWorksVariants variants={solucao.howItWorksVariants} />
            ) : (
              <ol className="space-y-4">
                {solucao.howItWorks.map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-lime text-lime-foreground flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {i + 1}
                    </div>
                    <p className="text-base text-foreground pt-1 leading-relaxed">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
              Pronto para acelerar seu buffet?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Agende uma demonstração e veja a AmeUp funcionando com os dados do
              seu buffet.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-lime text-lime-foreground hover:bg-lime/90"
            >
              <Link to="/cadastro-fornecedor">
                Agendar uma demonstração
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* Outras soluções */}
        <section className="py-16 md:py-20 bg-muted/30 border-t border-border/40">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10">
              Conheça outras soluções
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {outras.map((s) => {
                const OIcon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`/solucoes/${s.slug}`}
                    className="group block p-5 rounded-xl border border-border/60 bg-background hover:border-lime/50 hover:shadow-sm transition"
                  >
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-3">
                      <OIcon className="w-5 h-5 text-lime" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 group-hover:text-lime transition">
                      {s.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {s.tagline}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SolucaoPage;
